import type { BuildInput, CalculationResultDraft, PricingSummary, PricingSummaryAllowance } from '../../engines/types';
import { estimateMaterialRowPrice } from './priceEstimator';

function roundToNearest(value: number, step: number) {
  return Math.round(value / step) * step;
}

function buildAllowances(
  input: BuildInput,
  calculation: CalculationResultDraft,
  materialCostSek: number,
): PricingSummaryAllowance[] {
  const floorArea = input.dimensions.width * input.dimensions.length;
  const openingCount = input.openings.length;
  const heaterIsWood = input.thermalInputs.heaterType === 'wood';
  const professionalCostSek = calculation.professionalGuidance?.estimatedTotalCostSek ?? 0;

  const deliveryAndRounding = roundToNearest(
    Math.max(3500, floorArea * 320 + openingCount * 350 + (calculation.foundation ? 1200 : 0)),
    100,
  );
  const hiddenSupplies = roundToNearest(
    Math.max(2800, floorArea * 260 + (heaterIsWood ? 1200 : 900)),
    100,
  );

  const allowances: PricingSummaryAllowance[] = [
    {
      id: 'delivery-rounding',
      labelKey: 'domain_pricing_allowanceDeliveryRoundingLabel',
      amountSek: deliveryAndRounding,
      noteKey: 'domain_pricing_allowanceDeliveryRoundingNote',
    },
    {
      id: 'hidden-supplies',
      labelKey: 'domain_pricing_allowanceHiddenSuppliesLabel',
      amountSek: hiddenSupplies,
      noteKey: 'domain_pricing_allowanceHiddenSuppliesNote',
    },
  ];

  if (professionalCostSek > 0) {
    allowances.unshift({
      id: 'professional-services',
      labelKey: 'domain_pricing_allowanceProfessionalServicesLabel',
      amountSek: professionalCostSek,
      noteKey: 'domain_pricing_allowanceProfessionalServicesNote',
    });
  }

  const marketPackagingMargin = roundToNearest(
    Math.max(18000, floorArea * 1800 + (heaterIsWood ? 4500 : 2800) + materialCostSek * 0.06),
    500,
  );

  allowances.push({
    id: 'market-packaging',
    labelKey: 'domain_pricing_allowanceMarketPackagingLabel',
    amountSek: marketPackagingMargin,
    noteKey: 'domain_pricing_allowanceMarketPackagingNote',
  });

  return allowances;
}

export function calculatePricingSummary(input: BuildInput, calculation: CalculationResultDraft): PricingSummary {
  const materialCostSek = calculation.materials.reduce((sum, row) => (
    sum + estimateMaterialRowPrice(row, { mode: 'baseline' }).price
  ), 0);
  const allowances = buildAllowances(input, calculation, materialCostSek);
  const professionalCostSek = calculation.professionalGuidance?.estimatedTotalCostSek ?? 0;
  const diyBudgetSek = roundToNearest(
    materialCostSek + allowances
      .filter((allowance) => allowance.id !== 'market-packaging')
      .reduce((sum, allowance) => sum + allowance.amountSek, 0),
    500,
  );
  const marketPackageSek = roundToNearest(
    diyBudgetSek + (allowances.find((allowance) => allowance.id === 'market-packaging')?.amountSek ?? 0),
    500,
  );

  return {
    // The exact row sum, never rounded for display: every surface that quotes
    // the material cost (tab badge, spec pill, summary rail, the Price hero and
    // the itemised table footer) must show the same figure. A rounded headline
    // above a table that adds up exactly put two totals on one screen.
    materialCostSek: Math.round(materialCostSek),
    diyBudgetSek,
    marketPackageSek,
    professionalCostSek,
    allowances,
    comparatorNoteKey: 'domain_pricing_comparatorNote',
  };
}
